/**
 * CaptionRenderer - React component for animated caption overlays
 * Renders CaptionBlocks with Framer Motion animations
 * This is the UI preview that must match export output
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import { getPreset, presetToCssStyles } from '../lib/captionPresets';
import { getHighlightedWordIndex, isBlockVisible } from '../lib/captionTypes';

/**
 * Get animation variants based on preset animation type
 */
const getAnimationVariants = (animationType) => {
    switch (animationType) {
        case 'word-highlight':
            return {
                initial: { opacity: 1 },
                animate: { opacity: 1 },
                exit: { opacity: 0, transition: { duration: 0.15 } }
            };
        case 'scale-punch':
            return {
                initial: { scale: 0.8, opacity: 0 },
                animate: { scale: 1, opacity: 1 },
                exit: { scale: 0.9, opacity: 0 }
            };
        case 'fade-slide':
            return {
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0 },
                exit: { opacity: 0, y: -10 }
            };
        case 'pop-in':
            return {
                initial: { scale: 0, opacity: 0 },
                animate: { scale: 1, opacity: 1 },
                exit: { scale: 0.5, opacity: 0 }
            };
        case 'slide-up':
            return {
                initial: { opacity: 0, y: 30 },
                animate: { opacity: 1, y: 0 },
                exit: { opacity: 0, y: -20 }
            };
        case 'fade':
        default:
            return {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 }
            };
    }
};

/**
 * Single word with highlight animation
 */
const CaptionWord = ({
    word,
    isHighlighted,
    preset,
    wordIndex,
    animationType
}) => {
    const baseStyle = {
        display: 'inline-block',
        marginRight: '0.3em',
        transition: `all ${preset.animationDuration}s ${preset.animationEasing}`
    };

    const highlightStyle = isHighlighted ? {
        color: preset.highlightColor,
        transform: animationType === 'scale-punch' ? 'scale(1.15)' : 'scale(1)',
    } : {
        color: preset.textColor,
        transform: 'scale(1)',
    };

    return (
        <motion.span
            style={{ ...baseStyle, ...highlightStyle }}
            initial={{ opacity: 0, y: 10 }}
            animate={{
                opacity: 1,
                y: 0,
                transition: { delay: wordIndex * 0.02 }
            }}
        >
            {word.text}
        </motion.span>
    );
};

/**
 * Caption block container
 */
const CaptionBlock = ({
    block,
    currentTime,
    preset,
    containerWidth,
    containerHeight
}) => {
    const highlightedIndex = getHighlightedWordIndex(block, currentTime);
    const styles = presetToCssStyles(preset);
    const variants = getAnimationVariants(preset.animation);

    // Calculate position
    const positionStyle = {
        position: 'absolute',
        left: `${block.position.x}%`,
        top: `${block.position.y}%`,
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        maxWidth: '90%',
        padding: preset.bgColor !== 'transparent' ? '8px 16px' : '0',
        borderRadius: preset.bgColor !== 'transparent' ? '8px' : '0',
    };

    // Break words into lines based on maxWordsPerLine
    const lines = useMemo(() => {
        const result = [];
        const words = block.words;

        for (let i = 0; i < words.length; i += preset.maxWordsPerLine) {
            result.push(words.slice(i, i + preset.maxWordsPerLine));
        }
        return result;
    }, [block.words, preset.maxWordsPerLine]);

    return (
        <motion.div
            key={block.id}
            style={{ ...styles, ...positionStyle }}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: preset.animationDuration }}
        >
            {lines.map((lineWords, lineIdx) => (
                <div key={lineIdx} style={{ marginBottom: lineIdx < lines.length - 1 ? '4px' : 0 }}>
                    {lineWords.map((word, wordIdx) => {
                        const globalIdx = lineIdx * preset.maxWordsPerLine + wordIdx;
                        return (
                            <CaptionWord
                                key={`${block.id}-${globalIdx}`}
                                word={word}
                                isHighlighted={preset.wordByWord && globalIdx === highlightedIndex}
                                preset={preset}
                                wordIndex={globalIdx}
                                animationType={preset.animation}
                            />
                        );
                    })}
                </div>
            ))}
        </motion.div>
    );
};

/**
 * Main CaptionRenderer component
 * @param {Object} props
 * @param {Array} props.blocks - Array of CaptionBlocks
 * @param {number} props.currentTime - Current playback time in seconds
 * @param {string} props.globalPreset - Default preset ID
 * @param {number} props.containerWidth - Container width for positioning
 * @param {number} props.containerHeight - Container height for positioning
 */
const CaptionRenderer = ({
    blocks = [],
    currentTime = 0,
    globalPreset = 'karaoke',
    containerWidth = 1080,
    containerHeight = 1920,
    onBlockClick
}) => {
    // Find visible blocks at current time
    const visibleBlocks = useMemo(() => {
        return blocks.filter(block => isBlockVisible(block, currentTime));
    }, [blocks, currentTime]);

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: onBlockClick ? 'auto' : 'none',
                overflow: 'hidden'
            }}
        >
            <AnimatePresence mode="sync">
                {visibleBlocks.map(block => {
                    const preset = getPreset(block.preset || globalPreset);
                    return (
                        <CaptionBlock
                            key={block.id}
                            block={block}
                            currentTime={currentTime}
                            preset={preset}
                            containerWidth={containerWidth}
                            containerHeight={containerHeight}
                        />
                    );
                })}
            </AnimatePresence>
        </div>
    );
};

export default CaptionRenderer;
export { CaptionWord, CaptionBlock };
